// @ts-strict-ignore
import { logger } from "observability";
import maxBy from "lodash/maxBy";
import minBy from "lodash/minBy";
import toLower from "lodash/toLower";

import type {
  InterestRateOption,
  RatesResponse
} from "components/pages/Earn/types";
import type { CoinPriceOverview, PriceOverview } from "types/prices";
import { getHighestTieredRates } from "utils/earn";

export type GrowRates = {
  interestRates: InterestRateOption[];
  hasError: boolean;
  maxAPY: string;
  minAPY: string;
};

export type APY = [
  string, // 'BTC'
  number // 0.0532
];

export interface Price extends PriceOverview, CoinPriceOverview {}

export interface RateData {
  prices: Price[];
  apys: APY[];
  rates: RatesResponse;
}

// Any coins that need to be omitted from the Earn APY listings go here
// @see: https://gemini-spaceship.atlassian.net/browse/PUB-1560
const OMITTED_COINS = ["LUNA", "UST"];

export const formatGrowRates = ({
  prices,
  apys,
  rates
}: RateData): GrowRates => {
  try {
    const tieredRates = getHighestTieredRates(rates);

    const interestRates = apys.reduce<InterestRateOption[]>(
      (arr, [cryptoSymbol, apy]) => {
        // Omit select coins from the Earn APY listings
        if (OMITTED_COINS.includes(cryptoSymbol)) {
          return arr;
        }

        const lowerSymbol = toLower(cryptoSymbol);

        const cryptoInfo = prices.find((c) => toLower(c.coin) === lowerSymbol);
        const tierRates = tieredRates.find(
          (c) => toLower(c.symbol) === lowerSymbol
        )?.tierRates;

        if (!cryptoInfo) {
          logger.error(`Error getting cryptoInfo for ${cryptoSymbol}`);
        } else {
          arr.push({
            symbol: cryptoSymbol,
            apy,
            price: cryptoInfo.ask ?? 0,
            value: cryptoInfo.name,
            slug: cryptoInfo.slug,
            ...(tierRates ? { tierRates } : {})
          });
        }
        return arr;
      },
      []
    );

    interestRates.sort((a, b) => b.apy - a.apy);

    const [, maxInterestRateDecimal] = maxBy(apys, ([, apy]) => apy);
    const [, minInteresRateDecimal] = minBy(apys, ([, apy]) => apy);

    return {
      interestRates,
      hasError: false,
      maxAPY: (maxInterestRateDecimal * 100).toFixed(2),
      minAPY: (minInteresRateDecimal * 100).toFixed(2)
    };
  } catch (e) {
    logger.error("Error formatting earn rates", e);
    return {
      interestRates: [],
      hasError: true,
      maxAPY: undefined,
      minAPY: undefined
    };
  }
};
